import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getReturnTo,
  type LocationStateWithReturnTo,
} from '@sidecar/common/utils';
import {
  buildTicketPath,
  resolveTicketPaths,
  TICKET_ROUTE_KEY,
  type TicketRouteKey,
  type TicketRouteParams,
} from './ticket-route.utils';

export type TicketLocationState = LocationStateWithReturnTo;

export type TicketNavigateOptions = {
  ticketId?: string;
  replace?: boolean;
  returnTo?: string;
};

export const useTicketRouteNavigation = () => {
  const navigate = useNavigate();
  const params = useParams<TicketRouteParams>();
  const location = useLocation();
  const ticketPaths = useMemo(
    () => resolveTicketPaths(location.pathname),
    [location.pathname]
  );
  const returnTo = getReturnTo<TicketLocationState>(location.state);

  const navigateToRoute = useCallback(
    (routeKey: TicketRouteKey, options?: TicketNavigateOptions) => {
      if (!ticketPaths) return;

      const target = buildTicketPath({
        path: ticketPaths[routeKey],
        params,
        ticketId: options?.ticketId,
      });

      if (!target) return;

      const nextReturnTo = options?.returnTo ?? returnTo;

      navigate(
        { pathname: target, search: location.search },
        {
          replace: options?.replace,
          ...(nextReturnTo && {
            state: { returnTo: nextReturnTo } satisfies TicketLocationState,
          }),
        }
      );
    },
    [navigate, params, ticketPaths, returnTo, location.search]
  );

  const navigateToReturnOrRoot = useCallback(() => {
    if (returnTo) return navigate(returnTo);

    navigateToRoute(TICKET_ROUTE_KEY.root);
  }, [navigate, navigateToRoute, returnTo]);

  return {
    params,
    pathname: location.pathname,
    ticketPaths,
    returnTo,
    navigateToRoute,
    navigateToReturnOrRoot,
  };
};
